import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Card,
  Row,
  Col,
  Badge,
  Spinner,
  Alert,
  Table,
  Button,
  Form,
} from "react-bootstrap";
import moment from "moment";
import {
  getTicketsByOrgId,
  getTicketLogsByTicketId,
  getTasksByTicketId,
  getTicketThread,
  addTicketReply,
} from "../Api/TicketApiOrg";
import { useAuth } from "../Context/AuthContext";
import "./TicketInfoPage.css";
import { toast } from "react-toastify";

const Section = ({ title, children }) => (
  <div className="mb-4">
    <h6 className="section-title">{title}</h6>
    <div className="section-card">{children}</div>
  </div>
);

const InfoRow = ({ label, value, children }) => (
  <Row className="mb-2">
    <Col md={4} className="text-muted fw-semibold">
      {label}
    </Col>
    <Col md={8}>{children || value}</Col>
  </Row>
);

const EmptyState = ({ text }) => <div className="empty-state">{text}</div>;

/* ===================== HELPERS ===================== */

const getPriorityBadge = (priority) => {
  const variants = {
    critical: "danger",
    high: "warning",
    medium: "primary",
    low: "secondary",
    normal: "info",
  };
  return (
    <Badge bg={variants[priority?.toLowerCase()] || "secondary"}>
      {priority}
    </Badge>
  );
};

const getStatusBadge = (status) => {
  const variants = {
    open: "success",
    closed: "secondary",
    "in progress": "warning",
    resolved: "info",
  };
  return (
    <Badge bg={variants[status?.toLowerCase()] || "secondary"}>{status}</Badge>
  );
};

const formatDateTime = (date, time) => {
  if (!date) return "N/A";
  return `${moment(date).format("MMM DD, YYYY")} ${time?.substring(0, 8) || ""}`;
};

/* ===================== PAGE ===================== */

const TicketInfoPage = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [ticket, setTicket] = useState(null);
  const [ticketLogs, setTicketLogs] = useState([]);
  const [ticketTasks, setTicketTasks] = useState([]);

  const [loading, setLoading] = useState(true);

  const [messages, setMessages] = useState([]);
  const [attachments, setAttachments] = useState([]);

  const [replyBody, setReplyBody] = useState("");
  const [replyFiles, setReplyFiles] = useState([]);

  /* ===================== FETCH DATA ===================== */

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        const thread = await getTicketThread(ticketId);

        setTicket(thread.ticket);
        setMessages(thread.messages || []);
        setTicketLogs(thread.logs || []);
        setAttachments(thread.attachments || []);

        const tasks = await getTasksByTicketId(ticketId);
        setTicketTasks(tasks || []);
      } catch (err) {
        console.error("❌ Failed to load ticket info", err);
      } finally {
        setLoading(false);
      }
    };

    if (user?.organisationId) loadData();
  }, [ticketId, user]);

  /* ===================== UI ===================== */

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (!ticket) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">Ticket not found.</Alert>
      </Container>
    );
  }

  const handleSendReply = async () => {
    const formData = new FormData();
    formData.append("messageBody", replyBody);
    formData.append("senderEmail", user.email);

    replyFiles.forEach((file) => {
      formData.append("files", file);
    });

    await addTicketReply(ticketId, formData);

    toast.success("Reply sent");

    setReplyBody("");
    setReplyFiles([]);

    // Reload thread
    const thread = await getTicketThread(ticketId);
    setMessages(thread.messages);
  };

  return (
    <Container fluid className="ticket-info-page mt-3">
      {/* MAIN CARD */}
      <Card className="shadow-sm border-0">
        {/* HEADER */}
        <Card.Header className="bg-white border-bottom">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h5 className="mb-1 fw-bold">Ticket Information</h5>
              <Badge pill bg="dark">
                {ticket.ticketId}
              </Badge>
            </div>

            <div className="d-flex gap-2">
              {getStatusBadge(ticket.status)}
              {getPriorityBadge(ticket.priority)}
            </div>
          </div>
        </Card.Header>

        <Card.Body>
          {/* ================= BASIC INFO ================= */}
          <Section title="📝 Ticket Details">
            <InfoRow label="Ticket Name" value={ticket.ticketName} />
            <InfoRow label="Ticket Type">
              <Badge pill bg="info">
                {ticket.ticketType || "N/A"}
              </Badge>
            </InfoRow>
            <InfoRow label="Created By" value={ticket.createdBy} />
            <InfoRow label="Assigned To" value={ticket.assignedTo || "N/A"} />
            <InfoRow label="From Email" value={ticket.fromMail || "N/A"} />
            <InfoRow
              label="Ticket Time"
              value={moment(ticket.ticketTime).format("MMM DD, YYYY • HH:mm")}
            />
          </Section>

          {/* ================= DESCRIPTION ================= */}
          <Section title="💬 Conversation">
            {messages.length > 0 ? (
              messages.map((msg, index) => (
                <Card key={msg.messageGuid} className="mb-3">
                  <Card.Body>
                    <div className="d-flex justify-content-between mb-2">
                      <div>
                        <strong>{msg.fromName || msg.fromMail}</strong>
                        <div className="text-muted small">
                          {moment(msg.createdOnDate).format("MMM DD, YYYY")}{" "}
                          {msg.createdOnTime?.substring(0, 8)}
                        </div>
                      </div>

                      {msg.isInternal && (
                        <Badge bg="warning">Internal Note</Badge>
                      )}
                    </div>

                    <div
                      className="conversation-body"
                      dangerouslySetInnerHTML={{ __html: msg.body }}
                    />

                    {/* Attachments for this message */}
                    {attachments.filter((a) => a.ticketMessageId === msg.id)
                      .length > 0 && (
                      <div className="mt-2">
                        <strong>Attachments:</strong>
                        {attachments
                          .filter((a) => a.ticketMessageId === msg.id)
                          .map((a) => (
                            <div key={a.attachmentGuid}>📎 {a.fileName}</div>
                          ))}
                      </div>
                    )}
                  </Card.Body>
                </Card>
              ))
            ) : (
              <EmptyState text="No messages found." />
            )}
          </Section>

          {/* <Section title="✍️ Reply">
            <Form>
              <Form.Group className="mb-3">
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={replyBody}
                  onChange={(e) => setReplyBody(e.target.value)}
                />
              </Form.Group>

              <Button onClick={handleSendReply}>Send Reply</Button>
            </Form>
          </Section> */}

          {/* ================= ACTIVITY LOG ================= */}
          <Section title="📜 Ticket Activity Log">
            {ticketLogs.length > 0 ? (
              <Table responsive bordered hover size="sm" className="mt-2">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Action</th>
                    <th>Old</th>
                    <th>New</th>
                    <th>Worked By</th>
                    <th>Effort</th>
                    <th>Remarks</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {ticketLogs.map((log, idx) => (
                    <tr key={log.ticketLogGuid}>
                      <td>{idx + 1}</td>
                      <td>
                        <Badge bg="secondary">
                          {log.actionType?.replace(/_/g, " ")}
                        </Badge>
                      </td>
                      <td>{log.oldValue || "—"}</td>
                      <td>{log.newValue || "—"}</td>
                      <td>{log.changedBy}</td>
                      <td>
                        {log.effortValue
                          ? `${log.effortValue} ${log.effortUnit}`
                          : "—"}
                      </td>
                      <td className="text-muted">
                        {log.closureRemarks || "—"}
                      </td>
                      <td>
                        {formatDateTime(log.createdDate, log.createdTime)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <EmptyState text="No ticket activity found." />
            )}
          </Section>

          {/* ================= TASKS ================= */}
          <Section title="🧩 Related Tasks">
            {ticketTasks.length > 0 ? (
              ticketTasks.map((task) => (
                <Card key={task.taskId} className="task-card mb-3">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div>
                        <strong>{task.taskName}</strong>
                        <Badge bg="dark" className="ms-2">
                          {task.taskId}
                        </Badge>
                      </div>
                      <div className="d-flex gap-2">
                        {getStatusBadge(task.taskStatus)}
                        {getPriorityBadge(task.taskPriority)}
                      </div>
                    </div>

                    <div className="text-muted mb-2">
                      Assigned To: {task.assignedTo || "N/A"}
                    </div>

                    {task.logs?.length > 0 ? (
                      <Table bordered size="sm">
                        <thead className="table-light">
                          <tr>
                            <th>#</th>
                            <th>Type</th>
                            <th>Description</th>
                            <th>User</th>
                            <th>Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {task.logs.map((l, i) => (
                            <tr key={l.taskLogId}>
                              <td>{i + 1}</td>
                              <td>
                                <Badge bg="secondary">
                                  {l.logType?.replace(/_/g, " ")}
                                </Badge>
                              </td>
                              <td>{l.logDescription || "—"}</td>
                              <td>{l.assignedUserId}</td>
                              <td>
                                {formatDateTime(l.createdDate, l.createdTime)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    ) : (
                      <EmptyState text="No logs recorded for this task." />
                    )}
                  </Card.Body>
                </Card>
              ))
            ) : (
              <EmptyState text="No tasks created for this ticket." />
            )}
          </Section>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default TicketInfoPage;
